// ============================================================
// Datei: src/components/GuestRegistration.jsx
//
// Zweck:
// - Zentrale Komponente zum Erfassen von Gästen
// - Unterstützt normale Ressort-Regeln und Admin-Regeln
//
// ÄNDERUNGEN:
// - guestAccessMode unterstützt weiterhin "restricted" und "admin"
// - forceRestrictedGuestRules bleibt erhalten
// - forceAdminGuestRules bleibt erhalten
// - Rollenauswahl zeigt jetzt exakt die Rollen 55 bis 86
// - Es werden nicht mehr nur GAST_-Rollen angezeigt
// - Neue Rollen im Dropdown:
//   SUPERPARTNER, PARTNER,
//   GAST_OK, GAST_ANWOHNER, GAST_BAND, GAST_PETZI, GAST_FESTIVAL,
//   TECHNIKER, PRESSE
// - Reihenfolge entspricht der SQL-Liste
// - Fallback ergänzt, falls /roles nicht alle Rollen zurückliefert
// - Kasse-Ressortansicht kann weiterhin auf 2 Einträge ohne Rollenwahl gezwungen werden
// - Kasse-Adminansicht kann unbegrenzt mit Rollenwahl arbeiten
// - Admissions ohne "Kein Eintritt" werden weiterhin gefiltert
// - Backend-Fehlermeldungen werden sauber angezeigt
// ============================================================

import React, { useEffect, useMemo, useState } from "react";
import Api from "../Api";
import Toast from "./Toast";
import "../styles/guestRegistration.css";
import { validateGuestEntry } from "../utils/guestListRules";

// ============================================================
// DEFAULT-ROLLE FÜR NORMALE EINGESCHRÄNKTE GÄSTELISTE
//
// Wenn keine Rollenwahl erlaubt ist, wird automatisch GAST_OK
// verwendet. Diese ID muss zur Datenbank passen.
// ============================================================
const GAST_OK_ID = 80;

// ============================================================
// ERLAUBTE ROLLEN FÜR DIE GÄSTE-REGISTRATION
//
// Wichtig:
// - Diese IDs entsprechen deiner SQL-Tabelle role.
// - Nur diese Rollen sollen im Dropdown erscheinen.
// - Die Reihenfolge hier bestimmt auch die Reihenfolge im Select.
// ============================================================
const GUEST_REGISTRATION_ROLES = [
	{
		roleId: 55,
		name: "SUPERPARTNER",
		label: "Superpartner"
	},
	{
		roleId: 56,
		name: "PARTNER",
		label: "Partner"
	},
	{
		roleId: 80,
		name: "GAST_OK",
		label: "Gast-OK"
	},
	{
		roleId: 81,
		name: "GAST_ANWOHNER",
		label: "Gast-Anwohner"
	},
	{
		roleId: 82,
		name: "GAST_BAND",
		label: "Gast-Band"
	},
	{
		roleId: 83,
		name: "GAST_PETZI",
		label: "Gast-Petzi"
	},
	{
		roleId: 84,
		name: "GAST_FESTIVAL",
		label: "Gast-Festival"
	},
	{
		roleId: 85,
		name: "TECHNIKER",
		label: "Techniker"
	},
	{
		roleId: 86,
		name: "PRESSE",
		label: "Presse"
	}
];

const ALLOWED_GUEST_ROLE_IDS = GUEST_REGISTRATION_ROLES.map(
	(roleItem) => roleItem.roleId
);

const ALLOWED_GUEST_ROLE_NAMES = GUEST_REGISTRATION_ROLES.map(
	(roleItem) => roleItem.name
);

// ============================================================
// Helper: Text normalisieren
// ============================================================
const normalizeText = (value) => {
	return String(value || "").trim();
};

const normalizeUpper = (value) => {
	return normalizeText(value).toUpperCase();
};

// ============================================================
// Helper: Rollen-ID robust lesen
//
// Zweck:
// - Je nach Backend-DTO kann die ID roleId, role_id oder id heissen.
// ============================================================
const getRoleId = (roleItem) => {
	const rawId =
		roleItem?.roleId ??
		roleItem?.role_id ??
		roleItem?.id ??
		null;

	if (rawId === null || rawId === undefined || rawId === "") {
		return null;
	}

	const numberValue = Number(rawId);

	return Number.isNaN(numberValue) ? null : numberValue;
};

// ============================================================
// Helper: Rollenname robust lesen
//
// Zweck:
// - Je nach Backend-DTO kann der Rollenname role, name,
//   roleName, role_name oder authority heissen.
// ============================================================
const getRoleName = (roleItem) => {
	return normalizeUpper(
		roleItem?.role ||
		roleItem?.name ||
		roleItem?.roleName ||
		roleItem?.role_name ||
		roleItem?.authority ||
		""
	);
};

// ============================================================
// Helper: Label für Dropdown holen
//
// Zweck:
// - Rollen werden im Dropdown schöner angezeigt.
// - Die Speicherung erfolgt trotzdem über die roleId.
// ============================================================
const getRoleLabel = (roleItem) => {
	const roleId = getRoleId(roleItem);
	const roleName = getRoleName(roleItem);

	const configuredRole = GUEST_REGISTRATION_ROLES.find((allowedRole) => {
		return allowedRole.roleId === roleId || allowedRole.name === roleName;
	});

	if (configuredRole) {
		return configuredRole.label;
	}

	return roleName
		.replace("GAST_", "Gast-")
		.replaceAll("_", " ");
};

// ============================================================
// Helper: Prüfen, ob Rolle für Gäste erlaubt ist
//
// Wichtig:
// - Es wird nach ID und Name geprüft.
// - Dadurch funktioniert es auch, wenn das Backend entweder die ID
//   oder den Namen anders strukturiert liefert.
// ============================================================
const isAllowedGuestRole = (roleItem) => {
	const roleId = getRoleId(roleItem);
	const roleName = getRoleName(roleItem);

	return (
		ALLOWED_GUEST_ROLE_IDS.includes(roleId) ||
		ALLOWED_GUEST_ROLE_NAMES.includes(roleName)
	);
};

// ============================================================
// Helper: Rollen nach gewünschter SQL-Reihenfolge sortieren
// ============================================================
const sortGuestRoles = (a, b) => {
	const roleIdA = getRoleId(a);
	const roleIdB = getRoleId(b);

	const roleNameA = getRoleName(a);
	const roleNameB = getRoleName(b);

	const indexAById = ALLOWED_GUEST_ROLE_IDS.indexOf(roleIdA);
	const indexBById = ALLOWED_GUEST_ROLE_IDS.indexOf(roleIdB);

	const indexAByName = ALLOWED_GUEST_ROLE_NAMES.indexOf(roleNameA);
	const indexBByName = ALLOWED_GUEST_ROLE_NAMES.indexOf(roleNameB);

	const indexA =
		indexAById !== -1
			? indexAById
			: indexAByName;

	const indexB =
		indexBById !== -1
			? indexBById
			: indexBByName;

	const safeIndexA = indexA === -1 ? 999 : indexA;
	const safeIndexB = indexB === -1 ? 999 : indexB;

	return safeIndexA - safeIndexB;
};

// ============================================================
// Helper: API-Rollen mit Fallback-Rollen zusammenführen
//
// Zweck:
// - Falls /roles z. B. nur GAST_-Rollen liefert, werden
//   SUPERPARTNER, PARTNER, TECHNIKER und PRESSE trotzdem angezeigt.
// - Voraussetzung: Diese IDs existieren in der Datenbank.
// ============================================================
const mergeApiRolesWithFallbackRoles = (apiRoles) => {
	const mergedRoles = [];

	GUEST_REGISTRATION_ROLES.forEach((configuredRole) => {
		const apiRole = apiRoles.find((roleItem) => {
			const roleId = getRoleId(roleItem);
			const roleName = getRoleName(roleItem);

			return (
				roleId === configuredRole.roleId ||
				roleName === configuredRole.name
			);
		});

		if (apiRole) {
			mergedRoles.push({
				...apiRole,
				roleId: getRoleId(apiRole) ?? configuredRole.roleId,
				name: getRoleName(apiRole) || configuredRole.name
			});
			return;
		}

		// Fallback, falls der Endpoint diese Rolle nicht zurückliefert.
		mergedRoles.push({
			roleId: configuredRole.roleId,
			name: configuredRole.name
		});
	});

	return mergedRoles;
};

const GuestRegistration = ({
	refreshGuestList,
	guests = [],
	guestAccessMode = "auto",
	forceRestrictedGuestRules = false,
	forceAdminGuestRules = false
}) => {
	// ============================================================
	// USER CONTEXT
	// ============================================================
	const department = localStorage.getItem("department")?.toLowerCase();
	const role = localStorage.getItem("role");
	const recordedById = localStorage.getItem("volunteerId");

	const isAdmin = role === "ADMIN" || role === "ROLE_ADMIN";
	const isKasse = department === "kasse";
	const isOkProgramm = role === "OK" && department === "programm";

	// ============================================================
	// MODUS
	//
	// restricted:
	// - maximal 2 Einträge
	// - keine Rollenwahl
	//
	// admin:
	// - unbegrenzt
	// - Rollenwahl
	// ============================================================
	const isForcedRestricted =
		guestAccessMode === "restricted" || forceRestrictedGuestRules;

	const isForcedAdmin =
		guestAccessMode === "admin" || forceAdminGuestRules;

	const canRegisterUnlimitedGuests =
		isForcedAdmin ||
		(!isForcedRestricted && (isAdmin || isKasse || isOkProgramm));

	const canSelectGuestRole =
		isForcedAdmin ||
		(!isForcedRestricted && (isAdmin || isKasse || isOkProgramm));

	// ============================================================
	// STATE
	// ============================================================
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [admissionId, setAdmissionId] = useState("");
	const [roleId, setRoleId] = useState("");

	const [roles, setRoles] = useState([]);
	const [admissions, setAdmissions] = useState([]);
	const [existingGuests, setExistingGuests] = useState([]);
	const [toast, setToast] = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	// ============================================================
	// INITIAL LOAD
	// ============================================================
	useEffect(() => {
		if (canSelectGuestRole) {
			loadRoles();
		}

		loadAdmissions();
		loadMyGuests();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canSelectGuestRole]);

	// ============================================================
	// ROLLEN LADEN
	//
	// Wichtig:
	// - Es werden exakt die Rollen 55 bis 86 angezeigt.
	// - Falls der Backend-Endpoint einzelne Rollen nicht zurückgibt,
	//   werden sie aus GUEST_REGISTRATION_ROLES ergänzt.
	// ============================================================
	const loadRoles = async () => {
		try {
			const res = await Api.get("/roles");
			const apiRoles = Array.isArray(res.data) ? res.data : [];

			const allowedApiRoles = apiRoles.filter(isAllowedGuestRole);
			const mergedRoles = mergeApiRolesWithFallbackRoles(allowedApiRoles);

			setRoles(mergedRoles.sort(sortGuestRoles));
		} catch (error) {
			console.error("❌ Fehler beim Laden der Rollen:", error);

			// Falls /roles komplett fehlschlägt, werden die bekannten Rollen
			// trotzdem angezeigt. Die IDs müssen dafür in der Datenbank existieren.
			setRoles(GUEST_REGISTRATION_ROLES);
		}
	};

	// ============================================================
	// EINTRITTE LADEN
	//
	// "Kein Eintritt" wird bewusst nicht angezeigt.
	// ============================================================
	const loadAdmissions = async () => {
		try {
			const res = await Api.get("/admissions");

			const filteredAdmissions = Array.isArray(res.data)
				? res.data.filter((admissionItem) => {
					const value = admissionItem.admission?.toLowerCase().trim();
					return value && !value.includes("kein");
				})
				: [];

			setAdmissions(filteredAdmissions);
		} catch (error) {
			console.error("❌ Fehler beim Laden der Eintritte:", error);
			setAdmissions([]);
		}
	};

	// ============================================================
	// EIGENE GÄSTE LADEN
	//
	// Diese Liste wird für die 2-Einträge-Regel verwendet.
	// ============================================================
	const loadMyGuests = () => {
		if (!recordedById) {
			return;
		}

		Api.get(`/guests/ok/${recordedById}`)
			.then((res) => {
				setExistingGuests(Array.isArray(res.data) ? res.data : []);
			})
			.catch((error) => {
				console.error("❌ Fehler beim Laden eigener Gäste:", error);
				setExistingGuests([]);
			});
	};

	// ============================================================
	// BACKEND-FEHLERMELDUNG AUSLESEN
	// ============================================================
	const extractBackendMessage = (error) => {
		const data = error?.response?.data;

		if (!data) {
			return null;
		}

		if (typeof data === "string") {
			return data;
		}

		if (typeof data === "object") {
			return data.message || data.error || null;
		}

		return null;
	};

	// ============================================================
	// GÄSTE FÜR LIMITPRÜFUNG
	// ============================================================
	const guestsForValidation = useMemo(() => {
		if (existingGuests.length > 0) {
			return existingGuests;
		}

		return (guests || []).filter((guest) => {
			return String(guest.recordedById) === String(recordedById);
		});
	}, [existingGuests, guests, recordedById]);

	// ============================================================
	// SUBMIT
	// ============================================================
	const handleSubmit = async (event) => {
		event.preventDefault();

		if (isLoading) {
			return;
		}

		if (!firstName || !lastName || !admissionId) {
			setToast({
				type: "error",
				message: "Bitte Vorname, Nachname und Eintritt ausfüllen."
			});
			return;
		}

		if (canSelectGuestRole && !roleId) {
			setToast({
				type: "error",
				message: "Bitte eine Rolle auswählen."
			});
			return;
		}

		if (!recordedById) {
			setToast({
				type: "error",
				message: "Der Erfasser konnte nicht ermittelt werden. Bitte neu einloggen."
			});
			return;
		}

		const selectedAdmission = admissions.find((admission) => {
			return String(admission.admissionId) === String(admissionId);
		});

		if (!selectedAdmission) {
			setToast({
				type: "error",
				message: "Ungültiger Eintritt."
			});
			return;
		}

		if (!canRegisterUnlimitedGuests) {
			const check = validateGuestEntry(
				guestsForValidation,
				selectedAdmission.admission
			);

			if (!check.valid) {
				setToast({
					type: "error",
					message: check.message
				});
				return;
			}
		}

		// ========================================================
		// Rolle bestimmen
		//
		// - Adminmodus: gewählte Rolle
		// - Restricted-Modus: automatisch GAST_OK
		// ========================================================
		const finalRoleId = canSelectGuestRole
			? Number(roleId)
			: GAST_OK_ID;

		const guestData = {
			firstName: firstName.trim(),
			lastName: lastName.trim(),
			admissionId: Number(admissionId),
			roleId: finalRoleId,
			recordedById: Number(recordedById)
		};

		try {
			setIsLoading(true);

			await Api.post("/guests", guestData);

			setToast({
				type: "success",
				message: "Gast erfolgreich eingeschrieben 🎉"
			});

			setFirstName("");
			setLastName("");
			setAdmissionId("");
			setRoleId("");

			loadMyGuests();

			if (refreshGuestList) {
				await refreshGuestList();
			}
		} catch (error) {
			const status = error.response?.status;
			const backendMessage = extractBackendMessage(error);

			if (status === 409) {
				setToast({
					type: "warning",
					message: backendMessage || "Dieser Gast ist bereits eingeschrieben."
				});
				return;
			}

			if (backendMessage?.includes("Maximal")) {
				setToast({
					type: "error",
					message: backendMessage
				});
				return;
			}

			setToast({
				type: "error",
				message: backendMessage || "Fehler beim Einschreiben."
			});
		} finally {
			setIsLoading(false);
		}
	};

	// ============================================================
	// RENDER
	// ============================================================
	return (
		<>
			{toast && (
				<Toast
					{...toast}
					onClose={() => setToast(null)}
				/>
			)}

			<div className="guest-list-container guest-registration-container">
				<h3 className="guest-list-title">
					Gast einschreiben
				</h3>

				<form
					className="guest-form"
					onSubmit={handleSubmit}
				>
					<input
						type="text"
						placeholder="Vorname"
						value={firstName}
						onChange={(event) => setFirstName(event.target.value)}
						required
					/>

					<input
						type="text"
						placeholder="Nachname"
						value={lastName}
						onChange={(event) => setLastName(event.target.value)}
						required
					/>

					<select
						value={admissionId}
						onChange={(event) => setAdmissionId(event.target.value)}
						required
					>
						<option value="">
							Eintritt wählen
						</option>

						{admissions.map((admission) => (
							<option
								key={admission.admissionId}
								value={admission.admissionId}
							>
								{admission.admission}
							</option>
						))}
					</select>

					{/* ====================================================
					    Rollenauswahl
					    - Normal eingeschränkte Kasse-Gästeliste:
					      ausgeblendet
					    - Adminansicht:
					      Rollen 55 bis 86 sichtbar
					   ==================================================== */}
					{canSelectGuestRole && (
						<select
							value={roleId}
							onChange={(event) => setRoleId(event.target.value)}
							required
						>
							<option value="">
								Rolle wählen
							</option>

							{roles.map((roleItem) => {
								const currentRoleId = getRoleId(roleItem);

								return (
									<option
										key={currentRoleId}
										value={currentRoleId}
									>
										{getRoleLabel(roleItem)}
									</option>
								);
							})}
						</select>
					)}

					<button
						type="submit"
						disabled={isLoading}
					>
						{isLoading ? "Speichern..." : "Gast erfassen"}
					</button>
				</form>
			</div>
		</>
	);
};

export default GuestRegistration;